// STEP LIVE-STUDIO-CONSOLE-SPLIT-3 · Shared Notice editor state + action hook.
//
// 목적: Studio (SellerLiveControl inline Notice card) 와 Console
// (SellerLiveConsole modal) 의 Notice contract 가 100% 동일함을 Audit 로
// 확인 (PUT /seller/streaming/sessions/{sid}/notice body {text, image_url?,
// image_size?} + DELETE + /uploads kind=broadcast).  양쪽이 각자 useState +
// uploadImage + save + clear 를 직접 중복 작성한 부분을 하나의 hook 으로
// 통합.  UI 는 shell 마다 다르게 유지 (STEP §4 "UI 가 크게 다르다면 shared
// action/hook + 각 shell presentation 구조 허용").
//
// Canonical contract (실제 backend 확인 결과):
//   · PUT    /seller/streaming/sessions/{sid}/notice   body: {text, image_url?, image_size?}
//   · DELETE /seller/streaming/sessions/{sid}/notice
//   · POST   /uploads  (form: file + kind='broadcast')
//
// 금지:
//   · 신규 endpoint · 신규 collection · Overlay Notice renderer 변경 (STEP §4).
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * useNoticeEditor(sessionId, { onSaved, onCleared } = {})
 *
 *   sessionId  — streaming session id (`strm_xxx`).  없으면 action 들이 toast
 *                로 안내만 하고 noop.
 *   onSaved    — save/clear 성공 후 shell 쪽 side-effect (예: Studio 의
 *                loadDetail() · Console 의 setLiveCtx(prev,…)).
 *   onCleared  — clear 성공 후 side-effect.  미지정이면 onSaved 재사용.
 */
// localMode — 테스트 모드: 서버 PUT/DELETE/업로드 없이 로컬 상태만 반영.
const useNoticeEditor = (sessionId, { onSaved, onCleared, localMode = false } = {}) => {
  const [draft, setDraft] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageSize, setImageSize] = useState('medium');
  const [imageUploading, setImageUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Shell 이 server 로부터 live_notice snapshot 을 받았을 때 호출.
  // (useEffect 안에서 호출해도 안전하도록 setter 만 수행.)
  const hydrate = useCallback((current) => {
    setDraft(current?.text || '');
    setImageUrl(current?.image_url || '');
    setImageSize(current?.image_size || 'medium');
  }, []);

  const uploadImage = useCallback(async (file) => {
    if (!file) return null;
    if (!/^image\//.test(file.type)) { toast.error('이미지 파일만 업로드 가능해요.'); return null; }
    if (file.size > 5 * 1024 * 1024) { toast.error('이미지 크기는 5MB 이하여야 해요.'); return null; }
    if (localMode) {
      const url = await new Promise((res) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result || ''));
        fr.onerror = () => res('');
        fr.readAsDataURL(file);
      });
      setImageUrl(url);
      return url;
    }
    setImageUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('kind', 'broadcast');
      const { data } = await api.post('/uploads', form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = data?.url || '';
      setImageUrl(url);
      return url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || '이미지 업로드 실패');
      return null;
    } finally { setImageUploading(false); }
  }, [localMode]);

  const save = useCallback(async () => {
    if (!sessionId) { toast.error('LIVE 상태에서만 공지를 등록할 수 있어요.'); return null; }
    if (busy) return null;
    const t = (draft || '').trim();
    if (!t && !imageUrl) { toast.error('공지 내용 또는 이미지를 입력해주세요.'); return null; }
    if (localMode) {
      const n = { text: t, image_url: imageUrl || null, image_size: imageUrl ? imageSize : null,
                  updated_at: new Date().toISOString() };
      toast.success('테스트 공지 반영 (이 브라우저만)');
      onSaved?.(n);
      return n;
    }
    setBusy(true);
    try {
      const body = { text: t };
      if (imageUrl) { body.image_url = imageUrl; body.image_size = imageSize; }
      const { data } = await api.put(`/seller/streaming/sessions/${sessionId}/notice`, body);
      toast.success('LIVE 공지를 반영했어요.');
      onSaved?.(data?.live_notice || { text: t, image_url: imageUrl || null,
        image_size: imageUrl ? imageSize : null });
      return data?.live_notice || null;
    } catch (e) {
      toast.error(e?.response?.data?.detail || '공지 저장 실패');
      return null;
    } finally { setBusy(false); }
  }, [sessionId, busy, draft, imageUrl, imageSize, onSaved, localMode]);

  const clear = useCallback(async () => {
    if (!sessionId) { toast.error('LIVE 상태에서만 삭제할 수 있어요.'); return false; }
    if (busy) return false;
    if (!window.confirm('LIVE 공지를 삭제할까요?')) return false;
    setBusy(true);
    try {
      if (!localMode) await api.delete(`/seller/streaming/sessions/${sessionId}/notice`);
      setDraft('');
      setImageUrl('');
      setImageSize('medium');
      toast.info('공지를 삭제했어요.');
      (onCleared || onSaved)?.(null);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || '삭제 실패');
      return false;
    } finally { setBusy(false); }
  }, [sessionId, busy, onCleared, onSaved, localMode]);

  // eslint 가 hook 의존성을 복잡하게 추적하지 않도록 return 은 매 render 에
  // 안정적인 reference 를 유지한다 (useCallback / state primitives).
  return {
    draft, setDraft,
    imageUrl, setImageUrl,
    imageSize, setImageSize,
    imageUploading, busy,
    hydrate, uploadImage, save, clear,
  };
};

// `hydrate` 를 server state 변화에 자동 호출하고 싶은 shell 을 위한 convenience.
// 보통은 shell 쪽에서 `useEffect(() => hydrate(server), [...])` 를 직접 쓰면 되지만
// 여기서는 server snapshot 참조만 들어오면 자동 hydrate 하도록 wrapper 를 제공.
export const useNoticeEditorAutoHydrate = (sessionId, serverLiveNotice, opts) => {
  const ne = useNoticeEditor(sessionId, opts);
  useEffect(() => {
    ne.hydrate(serverLiveNotice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverLiveNotice?.text, serverLiveNotice?.image_url,
      serverLiveNotice?.image_size, serverLiveNotice?.updated_at]);
  return ne;
};

export default useNoticeEditor;
